import { type ActingSnapshot, createActing } from './acting';
import { type AnimationClip, Mesh, Object3D, Quaternion, Vector3 } from 'three';

export type CompanionState =
  | 'idle'
  | 'attentive'
  | 'listening'
  | 'thinking'
  | 'responding'
  | 'ready'
  | 'paused';
export interface CompanionActivity {
  state: CompanionState;
  animate: boolean;
  greeting: number;
  activityKey?: string;
  lookYaw?: number | null;
  lookPitch?: number | null;
}

const mix = (a: number, b: number, weight: number) => a + (b - a) * weight;
const ease = (value: number) => {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
};
const beat = (time: number, start: number, end: number, fade = 0.65) =>
  ease((time - start) / fade) * ease((end - time) / fade);
const restWeights = {
  work: 0,
  book: 0,
  write: 0,
  listen: 0,
  attention: 0,
  hello: 0,
  turn: 0,
  gaze: 0,
  tilt: 0,
  glance: 0,
  complete: 0,
  coat: 0,
  scope: 0,
  touch: 0,
  stow: 0,
  warmth: 0,
  focus: 0,
  curiosity: 0,
  stance: 0,
  saccade: 0,
};

// Non-repeating, band-limited life motion; small enough to read as balance,
// rather than a perpetual bob. All timing is on the visible character's clock.
const drift = (time: number) =>
  0.58 * Math.sin(time * 0.73) +
  0.27 * Math.sin(time * 1.31 + 1.8) +
  0.15 * Math.sin(time * 2.17 + 0.3);
const blinkCurve = (age: number) =>
  age < 0 || age > 0.29
    ? 0
    : age < 0.075
      ? ease(age / 0.075)
      : age < 0.105
        ? 1
        : 1 - ease((age - 0.105) / 0.185);

export interface BerryMotionSnapshot {
  weights: typeof restWeights;
  velocities: typeof restWeights;
  time: number;
  helloAt: number;
  completeAt: number;
  nodAt: number;
  previousState: CompanionState;
  delight: 'coat' | 'scope';
  delightAt: number;
  nextDelightAt: number;
  delightIndex: number;
  blinkAt: number;
  blinkIndex: number;
  greeting: number;
  activityKey?: string;
  lastActivityAt: number;
  nextStretchAt: number;
  saccadeAt: number;
  saccadeIndex: number;
  saccadeTarget: number;
  acting: ActingSnapshot;
}

/** One character, including its clothing and held props. Gaze leads the turn,
 * the notebook leads the pencil, and every target is interruptible mid-action.
 * Work stays face-first; a brief idle stretch can reveal more of the body. */
export function createBerryMotion(
  scene: Object3D,
  clips: AnimationClip[] = [],
) {
  const node = (name: string) => {
    const found = scene.getObjectByName(name);
    if (!found) throw new Error(`Companion rig is missing ${name}`);
    return found;
  };
  const rig = node('Berry'),
    body = node('Body'),
    crown = node('Crown');
  const eyes = [node('LeftEye'), node('RightEye')],
    brows = [node('LeftBrow'), node('RightBrow')];
  const pupils = [node('LeftEyeGaze'), node('RightEyeGaze')];
  const morphs: Mesh[] = [];
  scene.traverse((object) => {
    if (object instanceof Mesh && object.morphTargetDictionary)
      morphs.push(object);
  });
  const face = (name: string, value: number) => {
    for (const mesh of morphs) {
      const index = mesh.morphTargetDictionary![name];
      if (index !== undefined)
        mesh.morphTargetInfluences![index] = Math.max(0, Math.min(1, value));
    }
  };
  const acting = createActing(scene, clips),
    act = node('Acting');
  const bodyPosition = body.position.clone();
  const eyePositions = eyes.map((eye) => eye.position.clone()),
    browPositions = brows.map((brow) => brow.position.clone());
  const legs = [node('LeftLeg'), node('RightLeg')],
    legPositions = legs.map((leg) => leg.position.clone());
  const smile = node('Smile'),
    book = node('Notebook'),
    pencil = node('Pencil');
  const coat = node('DoctorCoat'),
    scope = node('Stethoscope');
  const arm = (side: string) => ({
    upper: node(side + 'UpperArm'),
    lower: node(side + 'Forearm'),
    elbow: node(side + 'Elbow'),
    hand: node(side + 'Hand'),
    sleeve: node(side + 'Sleeve'),
  });
  const left = arm('Left'),
    right = arm('Right');
  const yAxis = new Vector3(0, 1, 0),
    direction = new Vector3();
  const shoulder = new Vector3(),
    elbow = new Vector3(),
    hand = new Vector3(),
    touch = new Vector3();
  const pagePoint = new Vector3(),
    writingHand = new Vector3(),
    tip = new Vector3(-0.15, -0.159, 0.031);
  const writingRotation = new Quaternion(),
    restRotation = new Quaternion();
  const weights = { ...restWeights };
  const velocities = { ...weights };
  let time = 0,
    greeting = 0,
    helloAt = -10,
    completeAt = -10,
    nodAt = -10;
  let previousState: CompanionState = 'idle',
    activityKey: string | undefined;
  let delight: 'coat' | 'scope' = 'scope',
    delightAt = -20,
    nextDelightAt = 22,
    delightIndex = 0;
  let blinkAt = 3.8,
    blinkIndex = 0;
  let lastActivityAt = -10,
    nextStretchAt = 38;
  let saccadeAt = 1.4,
    saccadeIndex = 0,
    saccadeTarget = 0;

  const damp = (
    key: keyof typeof weights,
    target: number,
    speed: number,
    dt: number,
    animate: boolean,
  ) => {
    if (!animate) {
      weights[key] = target;
      velocities[key] = 0;
      return;
    }
    const offset = weights[key] - target,
      velocity = velocities[key];
    const spring = velocity + speed * offset,
      decay = Math.exp(-speed * dt);
    weights[key] = target + (offset + spring * dt) * decay;
    velocities[key] = (velocity - speed * spring * dt) * decay;
  };
  const segment = (mesh: Object3D, from: Vector3, to: Vector3) => {
    direction.subVectors(to, from);
    mesh.position.copy(from).add(to).multiplyScalar(0.5);
    mesh.scale.set(1, direction.length(), 1);
    mesh.quaternion.setFromUnitVectors(yAxis, direction.normalize());
  };
  const finishArm = (limb: typeof left, coatDrop: number) => {
    segment(limb.upper, shoulder, elbow);
    segment(limb.lower, elbow, hand);
    limb.elbow.position.copy(elbow);
    limb.hand.position.copy(hand);
    limb.sleeve.visible = coat.visible;
    limb.sleeve.position.copy(shoulder).lerp(elbow, 0.42);
    limb.sleeve.position.y -= coatDrop;
    limb.sleeve.quaternion.copy(limb.upper.quaternion);
    limb.sleeve.scale.set(1, shoulder.distanceTo(elbow) * 0.84, 1);
  };

  return {
    frameInterval() {
      const moving = Object.values(velocities).some(
        (value) => Math.abs(value) > 0.12,
      );
      return (
        1000 /
        (acting.moving() || moving || Math.abs(time - blinkAt) < 0.4 ? 60 : 30)
      );
    },
    // Carry the acting (including velocity) between Home and the editor's GL
    // surfaces. Input markers are ephemeral; this state is never persisted.
    snapshot(): BerryMotionSnapshot {
      return {
        weights: { ...weights },
        velocities: { ...velocities },
        time,
        helloAt,
        completeAt,
        nodAt,
        previousState,
        delight,
        delightAt,
        nextDelightAt,
        delightIndex,
        blinkAt,
        blinkIndex,
        greeting,
        activityKey,
        lastActivityAt,
        nextStretchAt,
        saccadeAt,
        saccadeIndex,
        saccadeTarget,
        acting: acting.snapshot(),
      };
    },
    restore(snapshot: BerryMotionSnapshot) {
      Object.assign(weights, snapshot.weights);
      Object.assign(velocities, snapshot.velocities);
      ({
        time,
        helloAt,
        completeAt,
        nodAt,
        previousState,
        delight,
        delightAt,
        nextDelightAt,
        delightIndex,
        blinkAt,
        blinkIndex,
      } = snapshot);
      ({
        greeting,
        activityKey,
        lastActivityAt,
        nextStretchAt,
        saccadeAt,
        saccadeIndex,
        saccadeTarget,
      } = snapshot);
      acting.restore(snapshot.acting);
    },
    dispose: acting.dispose,
    advance(delta: number, activity: CompanionActivity) {
      const dt = Math.min(Math.max(delta, 0), 0.05);
      const liveDt = activity.animate ? dt : 0;
      time += liveDt;
      const busy =
        activity.state === 'thinking' || activity.state === 'responding';
      const resting = activity.state === 'idle' || activity.state === 'ready';
      if (activity.state !== previousState) {
        if (
          activity.state === 'ready' &&
          (previousState === 'responding' || previousState === 'thinking')
        ) {
          completeAt = time;
          acting.start('Complete');
        }
        if (!resting || (previousState !== 'idle' && previousState !== 'ready'))
          nextStretchAt = Math.max(nextStretchAt, time + 38);
        if (!resting || (previousState !== 'idle' && previousState !== 'ready'))
          nextDelightAt = Math.max(nextDelightAt, time + 22);
        previousState = activity.state;
      }
      if (activity.greeting !== greeting) {
        greeting = activity.greeting;
        if (greeting > 0 && resting && greeting % 4 === 0) {
          acting.start('Stretch');
          nextStretchAt = time + 85;
        } else if (resting && (greeting % 4 === 2 || greeting % 4 === 3)) {
          delight = greeting % 4 === 2 ? 'scope' : 'coat';
          delightAt = time;
        } else if (greeting > 0) {
          helloAt = time;
          if (resting) acting.start('Greet');
        }
        nextDelightAt = time + 55;
      }
      // A short cameo after a quiet rest, then a long pause. Tapping the mascot
      // also invites these gestures. They never interrupt recording or writing.
      if (activity.animate && resting && time >= nextDelightAt) {
        delight = delightIndex++ % 2 === 0 ? 'scope' : 'coat';
        delightAt = time;
        nextDelightAt = time + 65;
      }
      if (activity.activityKey !== activityKey) {
        activityKey = activity.activityKey;
        // Acknowledge a new phrase after a gap, rather than nodding at the ASR
        // update rate. No transcript interpretation is implied by this gesture.
        if (
          activityKey &&
          activity.state === 'listening' &&
          time - lastActivityAt > 1.2 &&
          time - nodAt > 5
        ) {
          nodAt = time;
          acting.start('Acknowledge');
        }
        lastActivityAt = time;
      }
      if (
        activity.animate &&
        resting &&
        time >= nextStretchAt &&
        time - delightAt > 10
      ) {
        acting.start('Stretch');
        nextStretchAt = time + 85;
        nextDelightAt = time + 35;
      }
      acting.advance(
        liveDt,
        activity.animate,
        resting,
        activity.state === 'paused',
      );
      if (activity.animate && activity.lookYaw == null && time >= saccadeAt) {
        saccadeTarget = Math.sin(++saccadeIndex * 2.399) * 0.065;
        saccadeAt = time + 1.8 + (Math.sin(saccadeIndex * 7.13) + 1) * 1.2;
      }
      const alive = Number(activity.animate && activity.state !== 'paused');
      const idleLook = beat(time % 34, 4, 8.2) - beat(time % 34, 12, 16.8);
      const cameo = activity.animate && resting && time - delightAt < 7.4;
      const lookUp = alive * beat(time % 8.6, 5.7, 7.1, 0.4);
      damp('work', Number(busy), 7, dt, activity.animate);
      damp('book', Number(busy), 5.6, dt, activity.animate);
      damp(
        'write',
        Number(activity.state === 'responding') *
          ease((weights.book - 0.45) / 0.48),
        7.5,
        dt,
        activity.animate,
      );
      damp(
        'listen',
        Number(activity.state === 'listening'),
        6,
        dt,
        activity.animate,
      );
      damp(
        'attention',
        Number(activity.state === 'attentive' || activity.lookYaw != null),
        7,
        dt,
        activity.animate,
      );
      damp(
        'hello',
        Number(activity.animate && time - helloAt < 1.15),
        7,
        dt,
        activity.animate,
      );
      damp(
        'complete',
        alive * beat(time - completeAt, 0, 1.7, 0.45),
        9,
        dt,
        activity.animate,
      );
      damp('glance', weights.write * lookUp, 9, dt, activity.animate);
      damp(
        'coat',
        Number(cameo && delight === 'coat'),
        5.2,
        dt,
        activity.animate,
      );
      damp(
        'scope',
        Number(cameo && delight === 'scope'),
        5.2,
        dt,
        activity.animate,
      );
      damp(
        'touch',
        Number(cameo && time - delightAt < 1.9),
        6,
        dt,
        activity.animate,
      );
      damp(
        'stow',
        Number(cameo || weights.coat + weights.scope > 0.1) *
          (1 - weights.work),
        10,
        dt,
        activity.animate,
      );
      damp(
        'stance',
        Math.max(0, act.scale.x - 1) * Number(resting),
        6,
        dt,
        activity.animate,
      );
      damp(
        'warmth',
        0.12 * Number(resting) +
          Math.max(0, act.scale.y - 1) * 0.85 +
          0.28 * weights.hello,
        10,
        dt,
        activity.animate,
      );
      damp(
        'focus',
        busy
          ? 0.9
          : activity.state === 'listening'
            ? 0.6
            : activity.state === 'paused'
              ? 0.45
              : 0,
        8,
        dt,
        activity.animate,
      );
      damp('saccade', alive ? saccadeTarget : 0, 35, dt, activity.animate);
      const gazeTarget =
        activity.lookYaw != null
          ? activity.lookYaw
          : busy
            ? -0.36 + 0.28 * weights.glance
            : resting
              ? alive * idleLook * 0.43
              : activity.state === 'listening'
                ? -0.14
                : 0.12;
      damp('gaze', gazeTarget, 11, dt, activity.animate);
      damp(
        'curiosity',
        0.6 * weights.attention + 0.22 * weights.listen + 0.15 * weights.gaze,
        11,
        dt,
        activity.animate,
      );
      damp('turn', gazeTarget, 4.8, dt, activity.animate);
      const tiltTarget =
        activity.lookPitch != null
          ? activity.lookPitch
          : -0.075 * weights.listen +
            0.07 * weights.gaze +
            0.035 * weights.hello;
      damp('tilt', tiltTarget, 5, dt, activity.animate);
      const {
        work,
        book: liftBook,
        write,
        listen,
        attention,
        hello,
        complete,
        turn,
        gaze,
        tilt,
        glance,
      } = weights;
      const nod = alive * listen * beat(time - nodAt, 0, 0.85, 0.4);
      const breath =
        alive * Math.sin(time * 1.55 + 0.14 * Math.sin(time * 0.27));
      rig.rotation.y = turn + act.rotation.y;
      rig.position.set(0.015 * turn + act.position.x, 0.004 * breath, 0);
      body.position.copy(bodyPosition);
      body.position.y += act.position.y;
      body.rotation.set(
        0.075 * work -
          0.055 * attention -
          0.045 * complete -
          0.08 * glance +
          0.055 * nod,
        0.18 * (gaze - turn),
        tilt + alive * 0.013 * drift(time),
      );
      body.rotation.x += act.rotation.x;
      body.rotation.z += act.rotation.z;
      body.scale.set(1 - breath * 0.002, 1 + breath * 0.006, 1);
      body.updateMatrix();
      crown.rotation.z =
        alive * 0.018 * Math.sin(time * 1.55 - 0.5) - body.rotation.z * 0.3;
      smile.scale.set(1, 1, 1);
      const wonder = 0.12 * attention;
      const expressionTotal = Math.max(
        1,
        weights.warmth + weights.focus + wonder,
      );
      face('Smile', weights.warmth / expressionTotal);
      face('Focus', weights.focus / expressionTotal);
      face('Wonder', wonder / expressionTotal);
      face('Curious', weights.curiosity);
      brows.forEach((brow, index) => {
        brow.position.copy(browPositions[index]);
        brow.position.y +=
          attention * 0.014 + complete * 0.015 + hello * 0.01 - work * 0.006;
        brow.rotation.z =
          (index === 0 ? -1 : 1) * (0.05 * listen - 0.025 * work);
      });
      if (activity.animate && time > blinkAt + 0.29) {
        blinkIndex++;
        blinkAt =
          time +
          (blinkIndex % 7 === 3
            ? 0.12
            : 2.9 + (1 + Math.sin(blinkIndex * 2.399)) * 1.65);
      }
      const blink = alive * blinkCurve(time - blinkAt);
      face('Blink', blink);
      face(
        'Squint',
        (1 - blink) * (0.25 * weights.warmth + 0.12 * weights.focus),
      );
      eyes.forEach((eye, index) => {
        eye.scale.y = 1;
        eye.position.copy(eyePositions[index]);
        pupils[index].rotation.set(
          0.13 * work - 0.12 * glance + 0.02 * attention,
          0.28 * (gaze - turn) + weights.saccade,
          0,
        );
      });
      legs.forEach((leg, index) => {
        leg.position.copy(legPositions[index]);
        // Planted feet counter the weight transfer, rather than sliding with it.
        leg.position.x -= act.position.x;
        leg.rotation.set(
          0,
          turn * (index === 0 ? -0.12 : 0.08),
          (index === 0 ? -1 : 1) * hello * 0.045,
        );
      });

      // Clothing travels in from below the crop and settles on the same body.
      // Only hide it once it is entirely below the portrait, never crossfade it.
      const coatDrop = 1.05 * (1 - weights.coat);
      coat.visible = weights.coat > 0.012;
      coat.position.set(0, -coatDrop, 0.035 * Math.sin(Math.PI * weights.coat));
      coat.rotation.z = -0.1 * (1 - weights.coat);
      scope.visible = weights.scope > 0.012;
      scope.position.set(
        0.3 * (1 - weights.scope),
        -0.85 * (1 - weights.scope),
        0.14 * Math.sin(Math.PI * weights.scope),
      );
      scope.rotation.set(
        0,
        0.18 * (1 - weights.scope),
        -0.55 * (1 - weights.scope),
      );
      coat.updateMatrix();
      scope.updateMatrix();

      // Raise a tilted notebook into the existing close-up. The left hand is
      // attached to its edge; the right hand is solved from the actual pencil
      // tip and page plane, so writing does not happen in mid-air.
      book.position.set(
        mix(-0.53, -0.045, liftBook),
        mix(0.81, 1.015, liftBook) + 0.035 * Math.sin(Math.PI * liftBook),
        mix(0.06, 0.53, liftBook),
      );
      book.rotation.set(
        -0.82 * liftBook,
        -0.04 - 0.1 * liftBook,
        mix(0.18, 0.08, liftBook),
      );
      book.updateMatrix();
      const dressing = weights.touch * (1 - liftBook);
      shoulder.set(-0.43, 0.08, 0.035).applyMatrix4(body.matrix);
      elbow.set(
        mix(-0.6, -0.52, liftBook),
        mix(1.04, 1.07, liftBook),
        mix(0.04, 0.27, liftBook),
      );
      hand.set(-0.195, 0.035, 0.055).applyMatrix4(book.matrix);
      touch
        .set(-0.39, -0.02, 0.28)
        .applyMatrix4(coat.matrix)
        .applyMatrix4(body.matrix);
      hand.lerp(touch, dressing * weights.coat);
      finishArm(left, coatDrop);
      left.hand.rotation.set(
        -0.2 * liftBook,
        -0.1 * liftBook,
        -0.06 * liftBook,
      );

      const writing =
        write * alive * beat(time % 3.8, 0.2, 2.9, 0.3) * (1 - glance);
      const strokeX =
        writing * (0.018 * Math.sin(time * 11) + 0.005 * Math.sin(time * 18));
      const strokeY = writing * 0.008 * Math.cos(time * 12);
      pencil.rotation.set(
        0,
        -0.12 * work,
        -0.1 * work + hello * 0.12 * Math.sin(time * 8),
      );
      restRotation.copy(pencil.quaternion);
      writingRotation.copy(book.quaternion);
      pencil.quaternion.copy(restRotation).slerp(writingRotation, write);
      pagePoint
        .set(
          0.045 + strokeX,
          -0.045 + strokeY,
          0.055 + 0.018 * write * (1 - writing),
        )
        .applyMatrix4(book.matrix);
      writingHand
        .copy(tip)
        .applyQuaternion(pencil.quaternion)
        .multiplyScalar(-1)
        .add(pagePoint);
      shoulder.set(0.43, 0.08, 0.035).applyMatrix4(body.matrix);
      elbow.set(
        mix(0.6, 0.53, work),
        mix(1.08, 1.12, work),
        mix(0.025, 0.31, work),
      );
      hand
        .set(mix(0.57, 0.4, work), mix(0.88, 1.43, work), mix(0.04, 0.35, work))
        .lerp(writingHand, write);
      touch.set(0.56, 1.57, 0.22);
      hand.lerp(
        touch,
        Math.max(listen * 0.32, hello, act.scale.z - 1) * (1 - write),
      );
      touch
        .set(0.35, 0.09, 0.24)
        .applyMatrix4(scope.matrix)
        .applyMatrix4(body.matrix);
      hand.lerp(touch, dressing * weights.scope);
      touch
        .set(0.39, -0.02, 0.28)
        .applyMatrix4(coat.matrix)
        .applyMatrix4(body.matrix);
      hand.lerp(touch, dressing * weights.coat);
      finishArm(right, coatDrop);
      right.hand.quaternion.copy(pencil.quaternion);
      pencil.position.copy(hand);
      touch.set(0.1, 0.1, 0.075).applyMatrix4(book.matrix);
      pencil.position.lerp(touch, weights.stow);
      pencil.quaternion.slerp(book.quaternion, weights.stow);

      return {
        targetX: -0.07 * liftBook + 0.025 * turn,
        targetY:
          1.66 - 0.025 * liftBook + 0.006 * attention - 0.11 * weights.stance,
        height: 1.55 + 0.065 * liftBook + 0.025 * hello + 0.31 * weights.stance,
      };
    },
  };
}
